import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '../services/api';

const STATUS_OPTIONS = ['submitted', 'in_review', 'approved', 'active', 'closed'];
const STAGE_OPTIONS = [
  'Discovery Session',
  'Qualified',
  'Proposal Validation - Tech & Business',
  'FR Submission',
  'PO Recieved',
  'Delivery Implementation',
  'Customer Sign Off',
  'Cash Claim/Invoice Raised',
  'Launched',
];

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US');
}

function countBy(items, key) {
  const map = {};
  for (const item of items) {
    const v = item[key] || '(unknown)';
    map[v] = (map[v] || 0) + 1;
  }
  return Object.entries(map).sort((a, b) => b[1] - a[1]);
}

export default function AdminDashboard() {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [filterDealType, setFilterDealType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [updating, setUpdating] = useState({});

  useEffect(() => {
    adminService.getAllOpportunities({ limit: 1000 })
      .then(data => setAll(data.opportunities || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => [...new Set(all.map(o => o.partner_country).filter(Boolean))].sort(), [all]);
  const partners = useMemo(() => [...new Set(all.map(o => o.partner_company).filter(Boolean))].sort(), [all]);
  const dealTypes = useMemo(() => [...new Set(all.map(o => o.deal_type).filter(Boolean))].sort(), [all]);

  const filtered = useMemo(() => {
    let r = all;
    if (filterStatus) r = r.filter(o => o.status === filterStatus);
    if (filterCountry) r = r.filter(o => o.partner_country === filterCountry);
    if (filterPartner) r = r.filter(o => o.partner_company === filterPartner);
    if (filterDealType) r = r.filter(o => o.deal_type === filterDealType);
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      r = r.filter(o =>
        (o.opportunity_name || '').toLowerCase().includes(q) ||
        (o.company_name || '').toLowerCase().includes(q) ||
        (o.partner_company || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [all, filterStatus, filterCountry, filterPartner, filterDealType, searchText]);

  const stats = useMemo(() => ({
    total: filtered.length,
    submitted: filtered.filter(o => o.status === 'submitted').length,
    inReview: filtered.filter(o => o.status === 'in_review').length,
    approved: filtered.filter(o => o.status === 'approved').length,
    totalARR: filtered.reduce((s, o) => s + (parseFloat(o.arr_usd) || 0), 0)
  }), [filtered]);

  const byCountry = useMemo(() => countBy(all, 'partner_country'), [all]);
  const byPartner = useMemo(() => countBy(all, 'partner_company'), [all]);

  const handleFieldUpdate = useCallback(async (opportunityId, field, value) => {
    setUpdating(prev => ({ ...prev, [opportunityId + field]: true }));
    try {
      await adminService.updateOpportunity(opportunityId, { [field]: value });
      setAll(prev => prev.map(o => o.opportunity_id === opportunityId ? { ...o, [field]: value } : o));
    } catch {
      alert('Update failed');
    } finally {
      setUpdating(prev => ({ ...prev, [opportunityId + field]: false }));
    }
  }, []);

  const handleExport = async () => {
    try {
      const csv = await adminService.exportOpportunities();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cpn-opportunities-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Export failed');
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h2>All Opportunities</h2>
        <button onClick={handleExport} className="export-btn">↓ Export CSV</button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card"><h3>Total</h3><p className="stat-value">{stats.total}</p></div>
        <div className="stat-card pending"><h3>Submitted</h3><p className="stat-value">{stats.submitted}</p></div>
        <div className="stat-card"><h3>In Review</h3><p className="stat-value">{stats.inReview}</p></div>
        <div className="stat-card synced"><h3>Approved</h3><p className="stat-value">{stats.approved}</p></div>
        <div className="stat-card"><h3>Pipeline ARR</h3><p className="stat-value">{formatUSD(stats.totalARR)}</p></div>
      </div>

      {/* Breakdown panels */}
      <div className="breakdown-row">
        <div className="breakdown-panel">
          <h3>By Country</h3>
          <table className="breakdown-table">
            <thead><tr><th>Country</th><th>#</th></tr></thead>
            <tbody>
              {byCountry.map(([c, n]) => (
                <tr key={c} className={filterCountry === c ? 'active-filter-row' : 'clickable-row'}
                  onClick={() => setFilterCountry(filterCountry === c ? '' : c)}>
                  <td>{c}</td><td>{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="breakdown-panel">
          <h3>By Partner</h3>
          <table className="breakdown-table">
            <thead><tr><th>Partner</th><th>#</th></tr></thead>
            <tbody>
              {byPartner.map(([p, n]) => (
                <tr key={p} className={filterPartner === p ? 'active-filter-row' : 'clickable-row'}
                  onClick={() => setFilterPartner(filterPartner === p ? '' : p)}>
                  <td>{p}</td><td>{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <input type="text" placeholder="Search opportunity, company, partner…" value={searchText}
          onChange={e => setSearchText(e.target.value)} className="search-input" />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="submitted">Submitted</option>
          <option value="in_review">In Review</option>
          <option value="approved">Approved</option>
          <option value="active">Active</option>
        </select>
        <select value={filterDealType} onChange={e => setFilterDealType(e.target.value)}>
          <option value="">All Deal Types</option>
          {dealTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)}>
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterPartner} onChange={e => setFilterPartner(e.target.value)}>
          <option value="">All Partners</option>
          {partners.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {(filterStatus || filterCountry || filterPartner || filterDealType || searchText) && (
          <button className="clear-filters-btn" onClick={() => {
            setFilterStatus(''); setFilterCountry(''); setFilterPartner('');
            setFilterDealType(''); setSearchText('');
          }}>Clear</button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <>
          <p className="result-count">{filtered.length} opportunities</p>
          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>CPN ID</th>
                  <th>Partner</th>
                  <th>Country</th>
                  <th>Opportunity</th>
                  <th>Customer</th>
                  <th>Deal Type</th>
                  <th>Sub-Cat</th>
                  <th>Value Type</th>
                  <th>ARR (USD)</th>
                  <th>Rev Share</th>
                  <th>Stage</th>
                  <th>Status</th>
                  <th>Close Date</th>
                  <th>Submitted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(opp => (
                  <tr key={opp.opportunity_id}>
                    <td>{opp.cpn_id ? <span className="cpn-id">{opp.cpn_id}</span> : <span className="muted">—</span>}</td>
                    <td>{opp.partner_company || <span className="muted">—</span>}</td>
                    <td>{opp.partner_country || <span className="muted">—</span>}</td>
                    <td><strong>{opp.opportunity_name}</strong></td>
                    <td>{opp.company_name}</td>
                    <td>{opp.deal_type && <span className="tag">{opp.deal_type}</span>}</td>
                    <td>
                      <div className="tag-stack">
                        {opp.ps_sub_category && <span className="tag tag-sub">{opp.ps_sub_category}</span>}
                        {opp.resell_sub_category && <span className="tag tag-sub">{opp.resell_sub_category}</span>}
                      </div>
                    </td>
                    <td>{opp.deal_value_type || <span className="muted">—</span>}</td>
                    <td>{formatUSD(opp.arr_usd)}</td>
                    <td>{opp.revenue_share_pct != null ? opp.revenue_share_pct + '%' : <span className="muted">—</span>}</td>
                    <td>
                      <select
                        className="admin-inline-select"
                        value={opp.deal_stage || ''}
                        disabled={updating[opp.opportunity_id + 'deal_stage']}
                        onChange={e => handleFieldUpdate(opp.opportunity_id, 'deal_stage', e.target.value)}
                      >
                        {STAGE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>
                      <select
                        className={`admin-inline-select admin-status-select badge-${opp.status}`}
                        value={opp.status || ''}
                        disabled={updating[opp.opportunity_id + 'status']}
                        onChange={e => handleFieldUpdate(opp.opportunity_id, 'status', e.target.value)}
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>{opp.estimated_close_date}</td>
                    <td>{new Date(opp.created_at).toLocaleDateString()}</td>
                    <td><Link to={`/opportunities/${opp.opportunity_id}`} className="link-btn">Edit</Link></td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={16} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No results.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
